import React, { useState } from "react";
import { TailSpin } from "react-loader-spinner";
import { sendNotSentMessage } from "../../Api/apiUrls";

function SendMessageModal({ messageId, messageData, onClose, onSuccess }) {
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [editedNumber, setEditedNumber] = useState("");

  // Parse the message data safely
  const parsedData = React.useMemo(() => {
    try {
      const data = JSON.parse(messageData.data.replace(/'/g, '"'));
      setEditedNumber(data.number || ""); // Initialize editable number
      return data;
    } catch (e) {
      console.error("Error parsing message data:", e);
      return { number: "", parameters: [] };
    }
  }, [messageData.data]);

  const handleSendMessage = async () => {
    // Validate number before sending
    if (!editedNumber.trim()) {
      setError("Phone number is required");
      return;
    }

    setIsSending(true);
    setError(null);
    
    try {
      const response = await fetch(sendNotSentMessage, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: messageId,
          number: editedNumber // Use the edited number
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send message");
      }

      const result = await response.json();
      if (result.success) {
        setSuccess(true);
        setTimeout(onSuccess, 1500); // Give user time to see success message
      } else {
        throw new Error(result.message || "Failed to send message");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Resend Message</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              disabled={isSending}
            ></button>
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger">{error}</div>
            )}
            {success ? (
              <div className="alert alert-success">
                <i className="bi bi-check-circle-fill me-2"></i>
                Message resent successfully!
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="form-label">Phone Number*:</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editedNumber}
                    onChange={(e) => setEditedNumber(e.target.value)}
                    placeholder="Enter phone number"
                    disabled={isSending}
                  />
                  <small className="text-muted">Edit the phone number if needed</small>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Message Model:</label>
                  <input
                    type="text"
                    className="form-control"
                    value={messageData.model || "N/A"}
                    readOnly
                  />
                </div>

                {parsedData.parameters?.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Message Parameters:</label>
                    <ul className="list-group">
                      {parsedData.parameters.map((param, index) => (
                        <li key={index} className="list-group-item">
                          {param || "N/A"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mb-3">
                  <label className="form-label">Sending Method:</label>
                  <input
                    type="text"
                    className="form-control"
                    value={messageData.method || "N/A"}
                    readOnly
                  />
                </div>
                
                {messageData.error && (
                  <div className="mb-3">
                    <label className="form-label">Previous Error:</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={messageData.error}
                      readOnly
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSending}
            >
              Cancel
            </button>
            {!success && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendMessage}
                disabled={isSending || !editedNumber.trim()}
              >
                {isSending ? (
                  <>
                    <TailSpin 
                      color="#fff" 
                      height={20} 
                      width={20} 
                      wrapperStyle={{ display: 'inline-block', marginRight: '8px' }} 
                    /> 
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send-fill me-2"></i>
                    Resend Message
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SendMessageModal;